import { Injectable, Injector, StaticProvider } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { DECLARATIVE_FORMS_TEMPLATE_BUILDER_TOKEN } from './template-builder.token';
import { TemplateContext, TemplateInterpolator } from './template-interpolator';
import {
  TemplateEvaluationError,
  TemplateExpression,
  TemplatePart,
  isTemplateExpression,
  isTemplateLiteral,
} from './template.model';

/**
 * The template builder provides the functionality for
 * building an observable template interpolations for
 * a templated string in the context of declarative forms.
 *
 * It lets you build an `Observable` from any matched
 * template part (literal or expression), by passing it
 * to its `interpolate` method.
 * Therefore, it can be used to build an `Observable` for
 * an templated string, in combination with `interpolateTemplate`.
 *
 * @example
 * const template$ = of('hello {{first_name}}');
 * // Create an stream of values for the given template
 * const interpolated$ = templated$.pipe(
 *  interpolateTemplate((part) => this.templateBuilder.interpolate(part))
 * );
 *
 * @example
 * // You can also alter the stream of values that were interpolated
 * // by the template builder.
 * const template$ = of('contribution: {{amount}}');
 * template$.pipe(
 *  interpolateTemplate((part) =>
 *    this.templateBuilder.interpolate(part).pipe(
 *      // transforms value
 *      map((value) => (value === undefined ? '-' : value)),
 *      // catches potential errors like missing fields
 *      catchError((error) => of('-'))
 *    )
 *  )
 * );
 */
@Injectable()
export class TemplateBuilder {
  constructor(private injector: Injector) {}

  private _builders?: TemplateInterpolator[];

  /**
   * Chain of template builders.
   */
  public get builders(): TemplateInterpolator[] {
    if (!this._builders) {
      // Initialize chain from injector
      this._builders = this.injector.get(
        DECLARATIVE_FORMS_TEMPLATE_BUILDER_TOKEN,
        [],
      );
    }

    return this._builders;
  }

  /**
   * Convenience method for creating a stream of interpolated
   * values for the template `part`.
   * If no interpolation could be created, the returned
   * `Observable` will terminate with an `TemplateEvaluationError`.
   *
   * @param part Part of the template that should be interpolated.
   * @param opts Options for creating the default interpolation.
   *
   * @returns
   * An `Observable` that emits with changes of the `part` of the template
   * that is being observed.
   */
  public interpolate(part: TemplatePart): Observable<string> {
    return this.interpolator(part, this._prepareContext([]));
  }

  /**
   * Creates an `Observable` for the given `part` of a template
   * that emits with changes to the interpolated value of this
   * `part`.
   *
   * @param part Template part to observe
   * @param ctx Context for the template
   *
   * @returns
   * An `Observable` that emits changes of the interpolated value
   * of the given `part`.
   */
  private interpolator(
    part: TemplatePart,
    ctx: TemplateContext,
  ): Observable<string> {
    if (isTemplateLiteral(part)) {
      return of(part.raw);
    } else if (isTemplateExpression(part)) {
      return this.expression$(part, ctx);
    }

    return throwError(
      () => new TemplateEvaluationError(part, `Unknown template expression`),
    );
  }

  /**
   * Creates an `Observable` for the given `templateExpression`.
   *
   * @param templateExpression Template expression to be observed
   * @param ctx Context for the template
   *
   * @returns
   * An `Observable` that emits changed to the interpolated
   * value of the given `templateExpression`.
   */
  private expression$(
    templateExpression: TemplateExpression,
    ctx: TemplateContext,
  ): Observable<string> {
    // Find a builder for the expression and build
    for (const builder of this.builders) {
      if (builder.canCreate(templateExpression, ctx)) {
        return builder.expression$(templateExpression, ctx);
      }
    }

    // If no builder was found, throw error
    return throwError(
      () =>
        new TemplateEvaluationError(
          templateExpression,
          `No template node builder for expression ${templateExpression.expression}`,
        ),
    );
  }

  /**
   * Prepares the template context.
   *
   * @param providers Additional providers
   *
   * @returns
   * A context to pass to template interpolators
   */
  private _prepareContext(providers: StaticProvider[]): TemplateContext {
    const injector = Injector.create({
      providers,
      parent: this.injector,
    });

    return { injector };
  }
}
